import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { SignaturePad } from "@/components/SignaturePad";
import { BarcodeScanField } from "@/components/BarcodeScanField";

const LBL = "text-[10px] uppercase tracking-[0.1em] text-[#6B7280]";
const today = () => new Date().toLocaleDateString("en-CA");
const clock = () => new Date().toTimeString().slice(0, 5);
const emptyRow = () => ({ uhid: "", sample_id: "", nature_of_sample: "" });

/** One trip, many samples: shared header, then a fast one-line row per sample. */
export const TraceabilityBulk = ({ onDone, onClose }) => {
  const { options } = useAuth();
  const [head, setHead] = useState({
    delivered_from: "", delivered_by: "", delivered_to: "",
    received_on: today(), time_of_receipt: clock(),
  });
  const [rows, setRows] = useState([emptyRow()]);
  const [sigs, setSigs] = useState({ attender: null, technician: null });
  const [busy, setBusy] = useState(false);

  const setRow = (i, key, val) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));

  const addRow = () => setRows((rs) => [...rs, emptyRow()]);
  const dropRow = (i) => setRows((rs) => (rs.length === 1 ? [emptyRow()] : rs.filter((_, x) => x !== i)));

  const onRowKey = (e, i) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (i === rows.length - 1) addRow();
  };

  const saveSignature = async (role, payload) => {
    // stash the drawn signature; it is attached to every entry of this trip on submit
    setSigs((s) => ({ ...s, [role]: { ...payload, pending: true } }));
    toast.success(`${role === "attender" ? "Attender" : "Technician"} signature captured for this trip`);
  };

  const submit = async (e) => {
    e.preventDefault();
    const filled = rows.filter((r) => r.uhid.trim() || r.sample_id.trim());
    if (!filled.length) return toast.error("Add at least one sample");
    setBusy(true);
    try {
      const { data } = await api.post("/traceability/bulk", {
        ...head, rows: filled,
        attender_image: sigs.attender?.image || "",
        attender_signer_name: sigs.attender?.signer_name || "",
        technician_image: sigs.technician?.image || "",
        technician_signer_name: sigs.technician?.signer_name || "",
      });
      toast.success(`${data.created} samples logged — ${data.linked_to_samples} linked to existing samples`);
      setRows([emptyRow()]);
      setSigs({ attender: null, technician: null });
      onDone?.();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="rounded-md border border-[#E5E7EB] bg-white p-4 mt-4 w-full"
      data-testid="bulk-trace-form">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 className="font-heading font-bold text-[#111827]">Log a delivery trip</h2>
          <p className="text-xs text-[#6B7280] mt-0.5">
            Fill the handover details once, then add a line per sample. Press Enter to jump to a new line.
          </p>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={() => onClose?.()}
          data-testid="bulk-trace-close">Close</Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-4 [&>*]:min-w-0">
        <div>
          <Label className={LBL}>Delivered from *</Label>
          <select value={head.delivered_from} required data-testid="bulk-delivered-from"
            onChange={(e) => setHead({ ...head, delivered_from: e.target.value })}
            className="mt-1 h-10 w-full rounded-md border border-[#E5E7EB] bg-white px-2 text-sm">
            <option value="">Select…</option>
            {(options.delivered_from || []).map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <Label className={LBL}>Delivered by (attender) *</Label>
          <Input value={head.delivered_by} required data-testid="bulk-delivered-by" className="mt-1 h-10"
            onChange={(e) => setHead({ ...head, delivered_by: e.target.value })} />
        </div>
        <div>
          <Label className={LBL}>Delivered to</Label>
          <select value={head.delivered_to} data-testid="bulk-delivered-to"
            onChange={(e) => setHead({ ...head, delivered_to: e.target.value })}
            className="mt-1 h-10 w-full rounded-md border border-[#E5E7EB] bg-white px-2 text-sm">
            <option value="">Select…</option>
            {(options.technician || []).map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <Label className={LBL}>Received on *</Label>
          <Input type="date" value={head.received_on} required data-testid="bulk-received-on"
            className="mt-1 h-10" onChange={(e) => setHead({ ...head, received_on: e.target.value })} />
        </div>
        <div>
          <Label className={LBL}>Time of receipt *</Label>
          <Input type="time" value={head.time_of_receipt} required data-testid="bulk-time"
            className="mt-1 h-10" onChange={(e) => setHead({ ...head, time_of_receipt: e.target.value })} />
        </div>
      </div>

      <div className="mt-4">
        <BarcodeScanField testid="bulk-barcode" module="traceability" onResolved={(res) => {
          const p = res.prefill || {};
          setRows((rs) => {
            const idx = rs.findIndex((r) => !r.uhid.trim() && !r.sample_id.trim());
            const row = { uhid: p.patient_id || "", sample_id: p.sample_id || "",
                          nature_of_sample: p.sample_type || "" };
            if (idx === -1) return [...rs, row];
            return rs.map((r, i) => (i === idx ? row : r));
          });
        }} />
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm" data-testid="bulk-trace-rows">
          <thead className="bg-[#F3F4F6]">
            <tr>
              {["#", "ULID", "Sample ID", "Nature of sample", ""].map((h) => (
                <th key={h} className="text-left text-[10px] uppercase tracking-[0.08em] text-[#6B7280] px-2 py-2">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-[#E5E7EB]" data-testid={`bulk-row-${i}`}>
                <td className="px-2 py-1.5 text-xs text-[#6B7280]">{i + 1}</td>
                <td className="px-2 py-1.5">
                  <Input value={r.uhid} onChange={(e) => setRow(i, "uhid", e.target.value)}
                    onKeyDown={(e) => onRowKey(e, i)} className="h-9" data-testid={`bulk-row-uhid-${i}`} />
                </td>
                <td className="px-2 py-1.5">
                  <Input value={r.sample_id} onChange={(e) => setRow(i, "sample_id", e.target.value)}
                    onKeyDown={(e) => onRowKey(e, i)} className="h-9" data-testid={`bulk-row-sample-${i}`} />
                </td>
                <td className="px-2 py-1.5">
                  <select value={r.nature_of_sample} data-testid={`bulk-row-nature-${i}`}
                    onChange={(e) => setRow(i, "nature_of_sample", e.target.value)}
                    className="h-9 w-full rounded-md border border-[#E5E7EB] bg-white px-2 text-sm">
                    <option value="">Select…</option>
                    {(options.sample_nature || []).map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </td>
                <td className="px-2 py-1.5 text-right">
                  <button type="button" onClick={() => dropRow(i)} data-testid={`bulk-row-remove-${i}`}
                    className="p-1.5 rounded hover:bg-[#F3F4F6] text-[#C92A2A]"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Button type="button" variant="outline" size="sm" className="mt-2" onClick={addRow}
          data-testid="bulk-add-row"><Plus className="h-3.5 w-3.5 mr-1" /> Add line</Button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        <SignaturePad label="Attender signature (whole trip)" testid="bulk-sig-attender"
          signerLabel="Attender name" onSave={(p) => saveSignature("attender", p)} />
        <SignaturePad label="Receiving technician signature" testid="bulk-sig-technician"
          signerLabel="Technician name" onSave={(p) => saveSignature("technician", p)} />
      </div>

      <Button type="submit" disabled={busy} data-testid="bulk-trace-submit"
        className="mt-4 w-full bg-[#002FA7] hover:bg-[#00237C] text-white">
        {busy ? "Saving…" : `Log ${rows.filter((r) => r.uhid.trim() || r.sample_id.trim()).length} samples`}
      </Button>
    </form>
  );
};
